import React, { useState, ReactNode } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/system";

interface DropzoneProps {
  onDrop?: (files: FileList) => void;
  children: ReactNode;
  enabled: boolean;
}

const DropzoneContainer = styled(Box, {
  shouldForwardProp: prop => prop !== "isDragging",
})<{ isDragging: boolean }>(({ isDragging }) => ({
  position: "relative",
  width: "100%",
  border: isDragging ? "2px dashed #ccc" : undefined,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: isDragging ? "rgba(0, 0, 0, 0.1)" : "transparent",
  transition: "background-color 0.3s ease-in-out",
}));

const Overlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontSize: "1.5rem",
  fontWeight: "bold",
  borderRadius: "8px",
  zIndex: 10,
  pointerEvents: "none",
});

const Dropzone: React.FC<DropzoneProps> = ({ onDrop, children, enabled }) => {
  const [isDragging, setIsDragging] = useState(false);

  if (!enabled) {
    return <>{children}</>;
  }
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    if (onDrop) {
      onDrop(event.dataTransfer.files);
    }
  };

  return (
    <DropzoneContainer
      isDragging={isDragging}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && <Overlay>Drop files here</Overlay>}
    </DropzoneContainer>
  );
};

export default Dropzone;
